import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '../../ui';
import { useAuth } from '../../../stores';

const isDev = import.meta.env.DEV;

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const navigate = useNavigate();
  const { login, loginWithGoogle, loginWithApple, devLogin, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingApple, setIsLoadingApple] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();
    try {
      await login(data.email, data.password);
      navigate('/');
    } catch {
      // Error is handled by the store
    }
  };

  const handleDevLogin = () => {
    devLogin();
    navigate('/');
  };

  const handleGoogleSignIn = async () => {
    clearError();
    setIsLoadingGoogle(true);
    try {
      await loginWithGoogle();
      navigate('/');
    } catch {
      // Error is handled by the store
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleAppleSignIn = async () => {
    clearError();
    setIsLoadingApple(true);
    try {
      await loginWithApple();
      navigate('/');
    } catch {
      // Error is handled by the store
    } finally {
      setIsLoadingApple(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-white dark:bg-surface-800 rounded-2xl p-6 shadow-sm border border-surface-200 dark:border-surface-700">
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
                <EnvelopeIcon className="w-5 h-5" />
              </div>
              <Input
                type="email"
                placeholder="Email address"
                className="pl-10"
                {...register('email')}
                error={errors.email?.message}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
                <LockClosedIcon className="w-5 h-5" />
              </div>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                className="pl-10 pr-10"
                {...register('password')}
                error={errors.password?.message}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300"
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </motion.div>
        </div>

        <div className="mt-4 text-right">
          <Link
            to="/forgot-password"
            className="text-sm text-accent-600 dark:text-accent-400 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-xl p-4"
        >
          <p className="text-sm text-danger-600 dark:text-danger-400 text-center">
            {error}
          </p>
        </motion.div>
      )}

      <Button type="submit" size="lg" className="w-full" isLoading={isLoading}>
        Sign In
      </Button>

      {/* Social Sign-In Buttons */}
      <div className="space-y-3">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={handleGoogleSignIn}
          isLoading={isLoadingGoogle}
          disabled={isLoading || isLoadingApple}
          className="w-full flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={handleAppleSignIn}
          isLoading={isLoadingApple}
          disabled={isLoading || isLoadingGoogle}
          className="w-full flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          Continue with Apple
        </Button>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-surface-200 dark:border-surface-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-surface-50 dark:bg-surface-900 px-4 text-surface-500">
            or
          </span>
        </div>
      </div>

      <p className="text-center text-white drop-shadow-lg dark:text-surface-300">
        Don't have an account?{' '}
        <Link
          to="/register"
          className="font-medium text-teal-200 hover:text-teal-100 dark:text-teal-400 dark:hover:text-teal-300 hover:underline drop-shadow-md"
        >
          Create one
        </Link>
      </p>

      {/* Dev bypass - only shown in development */}
      {isDev && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="pt-4 border-t border-dashed border-surface-300 dark:border-surface-700"
        >
          <button
            type="button"
            onClick={handleDevLogin}
            className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
          >
            <BeakerIcon className="w-4 h-4" />
            Dev Mode: Skip Login
          </button>
          <p className="text-xs text-center text-surface-400 mt-2">
            Development only - bypasses Firebase auth
          </p>
        </motion.div>
      )}
    </form>
  );
}
